/**
 * Where to send the user once they finish signing in (VTK-1918).
 *
 * A protected route reached while signed out redirects to `/login` and, until now, threw the
 * intended destination away — so an employer who clicked a partner's emailed Section 2 link landed
 * on the dashboard and had to go back to the email and click it a second time.
 *
 * The destination cannot ride React Router state alone: the real OAuth path leaves the app entirely
 * (`window.location.href = oauthUrl`) and comes back on a fresh page load, which destroys router
 * state. It is kept in `sessionStorage` instead — the same store, tab lifetime and blast radius the
 * OAuth `state` nonce already uses.
 *
 * **Only same-origin paths are ever stored or returned.** The value is written from the address bar
 * and read back into `navigate()`, so an absolute or protocol-relative URL would be an open
 * redirect: somewhere to bounce a freshly-authenticated user off-site. `isSafeInternalPath` is the
 * single gate, applied on the way in *and* on the way out, so a value planted directly into storage
 * is rejected too.
 */

const STORAGE_KEY = 'postLoginRedirect';

/** Landing here after signing in would bounce the user straight back out, or round in a loop. */
const NEVER_RETURN_TO = ['/login', '/auth/granttoken', '/unauthorized', '/account-selection'];

/**
 * A path this app can navigate to internally: rooted, and not protocol-relative (`//evil.com`) or
 * otherwise absolute. Anything else is refused rather than sanitised — there is no legitimate
 * reason for a post-login destination to be off-site.
 */
export function isSafeInternalPath(path: string | null | undefined): boolean {
  const value = path?.trim();
  if (!value) return false;
  if (!value.startsWith('/')) return false;
  // `//host` and `/\host` are both read as protocol-relative by browsers.
  if (value.startsWith('//') || value.startsWith('/\\')) return false;
  const pathname = value.split(/[?#]/)[0];
  return !NEVER_RETURN_TO.includes(pathname);
}

/** Remember where the user was heading. A destination we would refuse to return to is not stored. */
export function setPostLoginRedirect(path: string | null | undefined): void {
  try {
    if (!isSafeInternalPath(path)) return;
    sessionStorage.setItem(STORAGE_KEY, path!.trim());
  } catch {
    // Storage can throw (private mode, quota). Losing the destination costs a second click, so it
    // must never break the sign-in it is decorating.
  }
}

/**
 * The stored destination, if any, cleared as it is read so a later sign-in cannot inherit a stale
 * one. Re-validated on the way out: storage is writable by anything running on this origin.
 */
export function consumePostLoginRedirect(): string | undefined {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    return isSafeInternalPath(value) ? value!.trim() : undefined;
  } catch {
    return undefined;
  }
}

/** Peek without consuming — for a step that must not spend the destination yet (account selection). */
export function hasPostLoginRedirect(): boolean {
  try {
    return isSafeInternalPath(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return false;
  }
}
